import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import OrderBelowMinPriceIcon from '../../../../../components/common/OrderBelowMinPriceIcon';
import SubtleBadge from '../../../../../components/common/SubtleBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { hasPermission, PERM } from '../../../../../utils/accessPermissions';
import formatCurrency from '../../../../../utils/formatCurrency';
import AdvanceTableResponsive from '../../../../../components/common/advance-table/AdvanceTableResponsive';
import AdvanceTableProvider from '../../../../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../../../../hooks/useAdvanceTable';
import { useUpdateBrandCorrectionAmountMutation } from '../../../../../slices/ordersApiSlice';
import { markLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';
import { toast } from 'react-toastify';
import { useOrderCorrectionDraft } from './OrderCorrectionDraftContext';
import { useOrderDetailViewOnly } from './OrderDetailViewOnlyContext';

const resolveItemUnitPrice = (item, field) => {
	const value = item[field];
	if (value === undefined || value === null) return null;
	const normalized = Number(value);
	return Number.isNaN(normalized) ? null : normalized;
};

const sumProductListLineTotal = (items, field) =>
	items.reduce((sum, item) => {
		const unitPrice = resolveItemUnitPrice(item, field);
		if (unitPrice === null) return sum;
		return sum + (Number(item.qty) || 0) * unitPrice;
	}, 0);

const sumProductListGrossProfit = (items) =>
	items.reduce((sum, item) => {
		const qty = Number(item.qty) || 0;
		const sellingPrice = Number(item.sellingPrice) || 0;
		const purchasingPrice = resolveItemUnitPrice(item, 'purchasingPrice') ?? 0;
		return sum + (sellingPrice - purchasingPrice) * qty;
	}, 0);

const formatProfitMarginRate = (rate) => `${(Math.round(Number(rate || 0) * 10000) / 100).toFixed(2)}%`;

const ProductListPriceMetricItem = ({ label, children, className = '' }) => (
	<div className={`order-modal-price-footer-chain__item ${className}`.trim()}>
		<div className='order-modal-price-footer-chain__label text-900'>{label}</div>
		<div className='order-modal-price-footer-chain__value text-900'>{children}</div>
	</div>
);

const CorrectionAmountSummary = ({ amount, label = '校正额合计' }) => (
	<ProductListPriceMetricItem label={label}>{formatCurrency(amount)}</ProductListPriceMetricItem>
);

const resolveItemCategoryName = (item) => {
	const category = (item?.category || '').trim();
	return category || '未分类';
};

const buildCategoryCorrectionKey = (brandName, categoryName) => {
	const brand = String(brandName ?? '').trim() || '未分类';
	const category = String(categoryName ?? '').trim() || '未分类';
	return `${brand}::${category}`;
};

const groupItemsByCategory = (items) => {
	const groupedMap = new Map();
	for (const item of Array.isArray(items) ? items : []) {
		const categoryName = resolveItemCategoryName(item);
		if (!groupedMap.has(categoryName)) groupedMap.set(categoryName, []);
		groupedMap.get(categoryName).push(item);
	}
	return Array.from(groupedMap.entries())
		.map(([categoryName, categoryItems]) => ({ categoryName, items: categoryItems }))
		.sort((a, b) => {
			if (a.categoryName === '未分类') return 1;
			if (b.categoryName === '未分类') return -1;
			return a.categoryName.localeCompare(b.categoryName, 'zh-CN');
		});
};

const distributeBrandCorrectionToCategories = (items, brandAmount) => {
	const groups = groupItemsByCategory(items);
	if (groups.length === 0) return [];
	const totalCost = sumProductListLineTotal(items, 'purchasingPrice');
	if (totalCost <= 0) {
		const share = brandAmount / groups.length;
		return groups.map((group) => ({ categoryName: group.categoryName, amount: share }));
	}
	return groups.map((group) => ({
		categoryName: group.categoryName,
		amount: brandAmount * (sumProductListLineTotal(group.items, 'purchasingPrice') / totalCost),
	}));
};

const ProductListPriceFooterChain = ({ purchasingTotal, sellingTotal, profitMarginRate, correctionSlot }) => {
	const isNegativeMargin = Number(profitMarginRate) < 0;

	return (
		<div className='order-modal-price-footer-chain'>
			{correctionSlot ? (
				<>
					{correctionSlot}
					<div className='order-modal-price-footer-chain__connector' aria-hidden='true' />
				</>
			) : null}
			<div className='order-modal-price-footer-chain__item'>
				<div className='order-modal-price-footer-chain__label text-900'>进价总价</div>
				<div className='order-modal-price-footer-chain__value text-900'>{formatCurrency(purchasingTotal)}</div>
			</div>
			<div className='order-modal-price-footer-chain__connector order-modal-price-footer-chain__connector--to-margin' aria-hidden='true' />
			<div className='order-modal-price-footer-chain__item order-modal-price-footer-chain__item--margin'>
				<div className='order-modal-price-footer-chain__label text-900'>利润率</div>
				<div
					className={`order-modal-price-footer-chain__value order-modal-price-footer-chain__value--margin ${isNegativeMargin ? 'text-danger' : 'text-900'}`}
				>
					{formatProfitMarginRate(profitMarginRate)}
				</div>
			</div>
			<div className='order-modal-price-footer-chain__connector order-modal-price-footer-chain__connector--from-selling' aria-hidden='true' />
			<div className='order-modal-price-footer-chain__item'>
				<div className='order-modal-price-footer-chain__label text-900'>销售总价</div>
				<div className='order-modal-price-footer-chain__value text-900'>{formatCurrency(sellingTotal)}</div>
			</div>
		</div>
	);
};

const computeTrueProfitMarginRate = (items, correctionAmount = 0) => {
	const purchasingTotal = sumProductListLineTotal(items, 'purchasingPrice');
	const correction = Number(correctionAmount) || 0;
	const marginBase = purchasingTotal + correction;
	if (marginBase <= 0) return 0;
	const trueProfit = sumProductListGrossProfit(items) - correction;
	return trueProfit / marginBase;
};

const buildProductListFooter = (items, correctionAmount = 0, { categoryCorrectionInput, correctionColumnId = 'productCategory' } = {}) => {
	if (!items?.length) return { footerCells: null, footerSpanGroups: null };

	const purchasingTotal = sumProductListLineTotal(items, 'purchasingPrice');
	const sellingTotal = sumProductListLineTotal(items, 'sellingPrice');
	const profitMarginRate = computeTrueProfitMarginRate(items, correctionAmount);
	const priceFooterChain = (
		<ProductListPriceFooterChain purchasingTotal={purchasingTotal} sellingTotal={sellingTotal} profitMarginRate={profitMarginRate} />
	);

	return {
		footerCells: {
			productCode: <span className='text-900'>合计</span>,
			productName: <CorrectionAmountSummary amount={correctionAmount} />,
			...(categoryCorrectionInput ? { [correctionColumnId]: categoryCorrectionInput } : {}),
		},
		footerSpanGroups: [
			{
				columnIds: ['purchasingPrice', 'sellingPrice', 'lineTotalPrice'],
				content: priceFooterChain,
				cellProps: { className: 'text-center align-middle' },
			},
		],
	};
};

const CATEGORY_SUBTOTAL_LEFT_COLUMN_IDS = ['productCode', 'productName', 'productCategory', 'qty', 'minimumSellingPrice'];
const CATEGORY_SUBTOTAL_PRICE_COLUMN_IDS = ['purchasingPrice', 'sellingPrice', 'lineTotalPrice'];

const CategoryCorrectionInput = ({ brandName, categoryName, amount, onRequestChange, label = '种类校正额' }) => (
	<div className='order-modal-category-correction'>
		<span className='order-modal-category-correction__label'>{label}</span>
		{onRequestChange ? (
			<div className='order-modal-category-correction__control input-group input-group-sm'>
				<span className='input-group-text px-1'>¥</span>
				<input
					key={`${buildCategoryCorrectionKey(brandName, categoryName)}-${Number(amount).toFixed(2)}`}
					type='text'
					inputMode='decimal'
					className='form-control text-end'
					defaultValue={Number(amount).toFixed(2)}
					onChange={(event) => {
						onRequestChange({ brandName, categoryName, rawValue: event.target.value, isTyping: true });
					}}
					onBlur={(event) => {
						const rawValue = event.target.value;
						const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
						if (Number.isNaN(normalizedValue)) return;
						if (Number(amount).toFixed(2) === Number(normalizedValue).toFixed(2)) return;
						onRequestChange({ brandName, categoryName, rawValue });
					}}
				/>
			</div>
		) : (
			<span className='order-modal-category-correction__value'>{formatCurrency(amount)}</span>
		)}
	</div>
);

const CategorySegmentSubtotalBar = ({ categoryName, brandName, amount, onRequestChange }) => (
	<div className='order-modal-category-subtotal'>
		<div className='order-modal-category-subtotal__head'>
			<span className='order-modal-category-subtotal__total text-900'>合计</span>
			<span className='order-modal-category-subtotal__divider text-muted' aria-hidden='true'>
				·
			</span>
			<span className='order-modal-category-subtotal__category text-800'>{categoryName}</span>
		</div>
		<CategoryCorrectionInput
			brandName={brandName}
			categoryName={categoryName}
			amount={amount}
			onRequestChange={onRequestChange}
		/>
	</div>
);

const buildBrandSegmentedTableRows = (brandName, items) => {
	const groups = groupItemsByCategory(items);
	const rows = [];
	for (const { categoryName, items: categoryItems } of groups) {
		for (const item of categoryItems) {
			rows.push({ rowType: 'item', ...item });
		}
		rows.push({
			rowType: 'categorySubtotal',
			id: `subtotal-${brandName}-${categoryName}`,
			categoryName,
			segmentItems: categoryItems,
		});
	}
	return rows;
};

const OrderProductListSummary = ({ items, correctionTotal }) => {
	if (!items?.length) return null;

	const purchasingTotal = sumProductListLineTotal(items, 'purchasingPrice');
	const sellingTotal = sumProductListLineTotal(items, 'sellingPrice');
	const profitMarginRate = computeTrueProfitMarginRate(items, correctionTotal);

	return (
		<div className='order-modal-product-order-summary'>
			<div className='order-modal-product-order-summary__inner'>
				<span className='order-modal-product-order-summary__title'>订单合计</span>
				<div className='order-modal-product-order-summary__prices'>
					<ProductListPriceFooterChain
						purchasingTotal={purchasingTotal}
						sellingTotal={sellingTotal}
						profitMarginRate={profitMarginRate}
						correctionSlot={<CorrectionAmountSummary amount={correctionTotal} />}
					/>
				</div>
			</div>
		</div>
	);
};

// 品牌分组子组件：单表按种类排序，段末插入合计行
const BrandGroupTable = ({ brandName, items, columns, getCategoryCorrectionAmount, onCategoryCorrectionRequest }) => {
	const tableData = useMemo(() => buildBrandSegmentedTableRows(brandName, items), [brandName, items]);

	const getBodySpanGroups = useCallback(
		(rowOriginal) => {
			if (rowOriginal?.rowType !== 'categorySubtotal') return null;
			const segmentItems = rowOriginal.segmentItems ?? [];
			const categoryCorrectionAmount = getCategoryCorrectionAmount(brandName, rowOriginal.categoryName);
			const purchasingTotal = sumProductListLineTotal(segmentItems, 'purchasingPrice');
			const sellingTotal = sumProductListLineTotal(segmentItems, 'sellingPrice');
			const profitMarginRate = computeTrueProfitMarginRate(segmentItems, categoryCorrectionAmount);
			return [
				{
					columnIds: CATEGORY_SUBTOTAL_LEFT_COLUMN_IDS,
					content: (
						<CategorySegmentSubtotalBar
							categoryName={rowOriginal.categoryName}
							brandName={brandName}
							amount={categoryCorrectionAmount}
							onRequestChange={onCategoryCorrectionRequest}
						/>
					),
					cellProps: { className: 'text-start align-middle' },
				},
				{
					columnIds: CATEGORY_SUBTOTAL_PRICE_COLUMN_IDS,
					content: (
						<ProductListPriceFooterChain
							purchasingTotal={purchasingTotal}
							sellingTotal={sellingTotal}
							profitMarginRate={profitMarginRate}
						/>
					),
					cellProps: { className: 'text-center align-middle' },
				},
			];
		},
		[brandName, getCategoryCorrectionAmount, onCategoryCorrectionRequest],
	);

	const segmentedColumns = useMemo(
		() =>
			columns.map((column) => ({
				...column,
				cell: (context) => {
					if (context.row.original?.rowType === 'categorySubtotal') return null;
					return typeof column.cell === 'function' ? column.cell(context) : undefined;
				},
			})),
		[columns],
	);

	const table = useAdvanceTable({
		data: tableData,
		columns: segmentedColumns,
		sortable: false,
		pagination: false,
	});

	return (
		<div className='mb-4'>
			<h6 className='order-modal-brand-group__head mb-2 fw-bold text-primary d-flex align-items-center'>
				<span className='d-flex align-items-center'>
					<FontAwesomeIcon icon='tag' className='me-2' />
					{brandName}
					<small className='text-muted ms-2'>({items.length} 个产品)</small>
				</span>
			</h6>
			<AdvanceTableProvider {...table}>
				<AdvanceTableResponsive
					cardListClassName='order-modal-product-card-list'
					headerClassName='bg-warning-subtle text-nowrap align-middle'
					rowClassName='text-nowrap align-middle'
					getBodySpanGroups={getBodySpanGroups}
					tableProps={{
						className: 'fs-10 fw-semibold mb-0 overflow-hidden',
					}}
				/>
			</AdvanceTableProvider>
		</div>
	);
};

const ProductListDetail = () => {
	const { order, isOrderReady } = useOrderContext();
	const viewOnly = useOrderDetailViewOnly();
	const { userInfo } = useSelector((state) => state.auth);
	const { orderItems = [], orderItemsGroupedByBrand = [], brandCorrectionAmounts = [], _id: orderOId, uiHints } = order ?? {};
	const isLegacyCompleted = Boolean(uiHints?.isLegacyCompletedBeforeFinalCostCutoff);
	// 最终成本已按当时的校正额算定，之后再改会让已确认成本与校正额合计对不上，故只读；要调整走「修正」审批
	const isFinalCostConfirmed = Boolean(order?.isFinalCostConfirmed);
	const isApprovalPassed = order?.approvalStatus === 'approved' || order?.approvalStatus === 'systemApproved';
	const canEditBrandCorrectionAmount =
		order?.viewerCapabilities?.canEditBrandCorrectionAmount ??
		(hasPermission(userInfo, PERM.ORDER_BRAND_CORRECTION_EDIT) && isApprovalPassed);
	const canEditCorrections =
		!viewOnly && Boolean(canEditBrandCorrectionAmount) && isApprovalPassed && !isLegacyCompleted && !isFinalCostConfirmed;
	const orderItemsHints = useMemo(() => uiHints?.orderItemsHints ?? [], [uiHints?.orderItemsHints]);
	const actionDisabled = !isOrderReady;

	const itemHintByIndex = useMemo(() => {
		const map = new Map();
		for (const hint of orderItemsHints) {
			map.set(hint.index, hint);
		}
		return map;
	}, [orderItemsHints]);

	const getItemHint = useCallback(
		(item) => {
			const index = orderItems.findIndex((row) => row === item || row._id === item?._id);
			return itemHintByIndex.get(index >= 0 ? index : 0) ?? { isBelowMinimumPrice: false, brandName: '未分类' };
		},
		[orderItems, itemHintByIndex],
	);

	const [updateBrandCorrectionAmount] = useUpdateBrandCorrectionAmountMutation();
	const { registerSave, setDraftStatus } = useOrderCorrectionDraft();
	const draftBrandCorrectionsRef = useRef({});
	const saveInFlightRef = useRef(false);
	const handleSaveCorrectionsRef = useRef(null);
	const [hasPendingCorrectionChanges, setHasPendingCorrectionChanges] = useState(false);
	const [isSavingCorrections, setIsSavingCorrections] = useState(false);
	const [inputVersion, setInputVersion] = useState(0);

	const useGroupedData = orderItemsGroupedByBrand && orderItemsGroupedByBrand.length > 0;

	const correctionAmountByBrandCategory = useMemo(() => {
		const map = new Map();
		const brandOnlyAmounts = new Map();

		if (Array.isArray(brandCorrectionAmounts) && brandCorrectionAmounts.length > 0) {
			brandCorrectionAmounts.forEach((row) => {
				const brand = String(row?.brand ?? '').trim() || '未分类';
				const category = String(row?.category ?? '').trim();
				const amount = Number(row.correctionAmount) || 0;
				if (category) {
					map.set(buildCategoryCorrectionKey(brand, category), amount);
				} else {
					brandOnlyAmounts.set(brand, amount);
				}
			});

			const brandGroups = useGroupedData
				? orderItemsGroupedByBrand
				: [...new Set(orderItemsHints.map((hint) => hint.brandName ?? '未分类'))].map((brandName) => ({
						brandName,
						items: orderItems.filter((item, index) => (itemHintByIndex.get(index)?.brandName ?? '未分类') === brandName),
					}));

			for (const brandGroup of brandGroups) {
				const brand = brandGroup.brandName || '未分类';
				if (!brandOnlyAmounts.has(brand)) continue;
				distributeBrandCorrectionToCategories(brandGroup.items, brandOnlyAmounts.get(brand)).forEach(({ categoryName, amount }) => {
					const key = buildCategoryCorrectionKey(brand, categoryName);
					if (!map.has(key)) map.set(key, amount);
				});
			}
			return map;
		}

		orderItems.forEach((item, index) => {
			const brand = itemHintByIndex.get(index)?.brandName ?? '未分类';
			const category = resolveItemCategoryName(item);
			const amount = Number(item.correctionAmount) || 0;
			if (amount === 0) return;
			const key = buildCategoryCorrectionKey(brand, category);
			map.set(key, (map.get(key) ?? 0) + amount);
		});
		return map;
	}, [brandCorrectionAmounts, orderItems, itemHintByIndex, orderItemsGroupedByBrand, orderItemsHints, useGroupedData]);

	useEffect(() => {
		draftBrandCorrectionsRef.current = {};
		setHasPendingCorrectionChanges(false);
		setIsSavingCorrections(false);
		setInputVersion((prev) => prev + 1);
	}, [orderOId]);

	const getCategoryCorrectionAmount = useCallback(
		(brandName, categoryName) => {
			void inputVersion;
			const key = buildCategoryCorrectionKey(brandName, categoryName);
			const rawValue = draftBrandCorrectionsRef.current[key];
			if (rawValue !== undefined) {
				const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
				if (!Number.isNaN(normalizedValue)) return normalizedValue;
			}
			return correctionAmountByBrandCategory.get(key) ?? 0;
		},
		[correctionAmountByBrandCategory, inputVersion],
	);

	const categoryCorrectionKeys = useMemo(() => {
		const keys = [];
		const brandGroups = useGroupedData
			? orderItemsGroupedByBrand
			: [...new Set(orderItemsHints.map((hint) => hint.brandName ?? '未分类'))].map((brandName) => ({
					brandName,
					items: orderItems.filter((item, index) => (itemHintByIndex.get(index)?.brandName ?? '未分类') === brandName),
				}));

		for (const brandGroup of brandGroups) {
			for (const categoryGroup of groupItemsByCategory(brandGroup.items)) {
				keys.push(buildCategoryCorrectionKey(brandGroup.brandName, categoryGroup.categoryName));
			}
		}
		return keys;
	}, [useGroupedData, orderItemsGroupedByBrand, orderItemsHints, orderItems, itemHintByIndex]);

	const updateHasPendingCorrectionChanges = useCallback(() => {
		const hasChanges = categoryCorrectionKeys.some((key) => {
			const rawValue = draftBrandCorrectionsRef.current[key];
			if (rawValue === undefined) return false;
			const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
			if (Number.isNaN(normalizedValue)) return false;
			const initialValue = correctionAmountByBrandCategory.get(key) ?? 0;
			return normalizedValue !== initialValue;
		});
		setHasPendingCorrectionChanges(hasChanges);
	}, [categoryCorrectionKeys, correctionAmountByBrandCategory]);

	const applyCategoryCorrectionDraft = useCallback(
		(brandName, categoryName, rawValue) => {
			const key = buildCategoryCorrectionKey(brandName, categoryName);
			draftBrandCorrectionsRef.current[key] = rawValue;
			updateHasPendingCorrectionChanges();
			setInputVersion((prev) => prev + 1);
		},
		[updateHasPendingCorrectionChanges],
	);

	const handleCategoryCorrectionChange = useCallback(
		({ brandName, categoryName, rawValue, isTyping = false }) => {
			// 打字时只置脏标记让保存按钮立刻出现：写入草稿会改动 amount 使 input key 变化而失焦，故仍留到失焦处理。
			// 只置 true 不置 false，避免把其他种类已暂存的改动一起标成已保存；准确值由失焦后的 updateHasPendingCorrectionChanges 重算
			if (isTyping) {
				const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
				if (Number.isNaN(normalizedValue)) return;
				const key = buildCategoryCorrectionKey(brandName, categoryName);
				if (normalizedValue !== (correctionAmountByBrandCategory.get(key) ?? 0)) setHasPendingCorrectionChanges(true);
				return;
			}
			applyCategoryCorrectionDraft(brandName, categoryName, rawValue);
		},
		[applyCategoryCorrectionDraft, correctionAmountByBrandCategory],
	);

	const columns = useMemo(
		() => [
			{
				id: 'productCode',
				accessorKey: 'productCode',
				header: '货号',
				meta: {
					card: { role: 'primary' },
					headerProps: { className: 'text-900 text-start', style: { width: '12%' } },
					cellProps: {
						className: 'fw-semibold text-start',
						style: { textAlign: 'left', paddingLeft: '6px', width: '12%' },
					},
				},
				cell: ({ row: { original } }) => {
					const { productCode } = original;
					let itemIndex = orderItems.findIndex((row) => row === original);
					if (itemIndex < 0 && original?._id) {
						itemIndex = orderItems.findIndex((row) => row._id && String(row._id) === String(original._id));
					}
					if (itemIndex < 0 && productCode) {
						const code = String(productCode).trim();
						itemIndex = orderItems.findIndex((row) => String(row?.productCode || '').trim() === code);
					}
					return (
						<div
							className='d-inline-flex align-items-center flex-nowrap'
							style={{ paddingLeft: '6px' }}
							data-order-item-index={itemIndex >= 0 ? itemIndex : undefined}
							data-order-product-code={productCode || undefined}
							id={itemIndex >= 0 ? `order-product-item-${itemIndex}` : undefined}
						>
							<Link className='text-800 text-decoration-none' to='#!'>
								{productCode}
							</Link>
							{getItemHint(original).isBelowMinimumPrice && <OrderBelowMinPriceIcon className='ms-1 flex-shrink-0 fs-11' />}
						</div>
					);
				},
			},
			{
				id: 'productName',
				accessorKey: 'name',
				header: '产品名称',
				meta: {
					card: { role: 'customer' },
					headerProps: { className: 'text-900 text-start', style: { width: '20%' } },
					cellProps: { className: 'text-start', style: { width: '20%' } },
				},
				cell: ({ row: { original } }) => {
					return <Link className='text-800'>{original.name}</Link>;
				},
			},
			{
				id: 'productBrand',
				accessorFn: (row) => getItemHint(row).brandName,
				header: '品牌',
				meta: {
					card: { role: useGroupedData ? 'hide' : undefined },
					headerProps: { className: 'text-900 text-start', style: { width: '10%' } },
					cellProps: {
						className: 'text-start',
						style: { verticalAlign: 'middle', width: '10%' },
					},
				},
				cell: ({ row: { original } }) => {
					const brandName = getItemHint(original).brandName;
					if (brandName === '未分类') {
						return <span className='text-muted'>{brandName}</span>;
					}
					return <div className='d-flex align-items-center'>{brandName}</div>;
				},
			},
			{
				id: 'productCategory',
				accessorFn: (row) => (row.category || '').trim(),
				header: '种类',
				meta: {
					headerProps: { className: 'text-900 text-start', style: { width: '10%' } },
					cellProps: {
						className: 'text-start',
						style: { verticalAlign: 'middle', width: '10%' },
					},
				},
				cell: ({ row: { original } }) => {
					const categoryName = (original.category || '').trim();
					if (!categoryName) {
						return <span className='text-muted'>-</span>;
					}
					return <div className='d-flex align-items-center'>{categoryName}</div>;
				},
			},
			{
				accessorKey: 'qty',
				header: '数量',
				meta: {
					headerProps: { className: 'text-900 text-center', style: { width: '6%' } },
					cellProps: { className: 'text-center', style: { width: '6%' } },
				},
				cell: ({ row: { original } }) => {
					return <span>{original.qty}</span>;
				},
			},
			{
				accessorKey: 'minimumSellingPrice',
				header: '销售底价',
				meta: {
					headerProps: { className: 'text-900 text-end', style: { width: '9%' } },
					cellProps: { className: 'text-end', style: { width: '9%' } },
				},
				cell: ({ row: { original } }) => {
					const minimumSellingPrice = original.minimumSellingPrice;
					if (minimumSellingPrice === undefined || minimumSellingPrice === null || Number.isNaN(Number(minimumSellingPrice))) {
						return <span className='text-muted'>-</span>;
					}
					return <span>{formatCurrency(minimumSellingPrice)}</span>;
				},
			},
			{
				accessorKey: 'purchasingPrice',
				header: '进价',
				meta: {
					headerProps: { className: 'text-900 text-end', style: { width: '9%' } },
					cellProps: { className: 'text-end', style: { width: '9%' } },
				},
				cell: ({ row: { original } }) => {
					const purchasingPrice = original.purchasingPrice;
					if (purchasingPrice === undefined || purchasingPrice === null || Number.isNaN(Number(purchasingPrice))) {
						return <span className='text-muted'>-</span>;
					}
					return <span>{formatCurrency(purchasingPrice)}</span>;
				},
			},
			{
				accessorKey: 'sellingPrice',
				header: '单价',
				meta: {
					headerProps: { className: 'text-900 text-end', style: { width: '10%' } },
					cellProps: { className: 'text-end', style: { width: '10%' } },
				},
				cell: ({ row: { original } }) => {
					const { sellingPrice, minimumSellingPrice } = original;
					if (getItemHint(original).isBelowMinimumPrice) {
						return (
							<div>
								<div>
									<del className='me-2 fw-bolder'>{formatCurrency(minimumSellingPrice)}</del>
									<span className='me-2 fw-bolder text-danger'>{formatCurrency(sellingPrice)}</span>
								</div>
								<div>
									<SubtleBadge bg='danger'>降价{formatCurrency(Number(sellingPrice - minimumSellingPrice))}</SubtleBadge>
								</div>
							</div>
						);
					}
					return <span className='me-2 fw-bolder text-800'>{formatCurrency(sellingPrice)}</span>;
				},
			},
			{
				id: 'lineTotalPrice',
				accessorKey: 'totalPrice',
				header: '总价',
				meta: {
					card: { role: 'footer' },
					headerProps: { className: 'text-900 text-end', style: { width: '14%' } },
					cellProps: { className: 'text-end', style: { width: '14%' } },
				},
				cell: ({ row: { original } }) => {
					const { qty, sellingPrice, minimumSellingPrice } = original;
					if (getItemHint(original).isBelowMinimumPrice) {
						return (
							<div>
								<div>
									<del className='me-2'>{formatCurrency(qty * minimumSellingPrice)}</del>
									<span className='fw-bolder text-danger'>{formatCurrency(qty * sellingPrice)}</span>
								</div>
								<div>
									<SubtleBadge bg='danger'>共计优惠{formatCurrency(Number(minimumSellingPrice * qty - sellingPrice * qty))}</SubtleBadge>
								</div>
							</div>
						);
					}
					return <span>{formatCurrency(qty * sellingPrice)}</span>;
				},
			},
		],
		[useGroupedData, getItemHint, orderItems],
	);

	const resolveDraftCorrectionAmount = (key) => {
		const rawValue = draftBrandCorrectionsRef.current[key];
		if (rawValue !== undefined) {
			const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
			if (!Number.isNaN(normalizedValue)) return normalizedValue;
		}
		return correctionAmountByBrandCategory.get(key) ?? 0;
	};

	const getPendingBrandCorrectionUpdates = () => {
		const brandGroups = useGroupedData
			? orderItemsGroupedByBrand
			: [...new Set(orderItemsHints.map((hint) => hint.brandName ?? '未分类'))].map((brandName) => ({
					brandName,
					items: orderItems.filter((item, index) => (itemHintByIndex.get(index)?.brandName ?? '未分类') === brandName),
				}));

		const changedBrands = new Set();
		for (const brandGroup of brandGroups) {
			const brandName = String(brandGroup.brandName ?? '').trim() || '未分类';
			for (const categoryGroup of groupItemsByCategory(brandGroup.items)) {
				const key = buildCategoryCorrectionKey(brandName, categoryGroup.categoryName);
				const rawValue = draftBrandCorrectionsRef.current[key];
				if (rawValue === undefined) continue;
				const normalizedValue = rawValue === '' ? 0 : Number(rawValue);
				if (Number.isNaN(normalizedValue)) continue;
				const initialValue = correctionAmountByBrandCategory.get(key) ?? 0;
				if (normalizedValue !== initialValue) changedBrands.add(brandName);
			}
		}
		if (changedBrands.size === 0) return [];

		const updates = [];
		for (const brandGroup of brandGroups) {
			const brandName = String(brandGroup.brandName ?? '').trim() || '未分类';
			if (!changedBrands.has(brandName)) continue;
			for (const categoryGroup of groupItemsByCategory(brandGroup.items)) {
				const categoryName = String(categoryGroup.categoryName ?? '').trim() || '未分类';
				const key = buildCategoryCorrectionKey(brandName, categoryName);
				updates.push({
					brand: brandName,
					category: categoryName,
					correctionAmount: resolveDraftCorrectionAmount(key),
				});
			}
		}
		return updates;
	};

	const savePendingCorrections = async () => {
		const updates = getPendingBrandCorrectionUpdates();
		if (updates.length === 0) return;

		markLocalOrderMutation(orderOId);
		const data = await updateBrandCorrectionAmount({
			orderId: orderOId,
			corrections: updates,
		}).unwrap();
		draftBrandCorrectionsRef.current = {};
		setHasPendingCorrectionChanges(false);
		setInputVersion((v) => v + 1);
		return data;
	};

	const handleSaveCorrections = async () => {
		if (!orderOId) return null;
		if (saveInFlightRef.current) {
			toast.warn('校正额保存中，请稍候');
			throw new Error('校正额保存中，请稍候');
		}
		if (document.activeElement instanceof HTMLInputElement) {
			document.activeElement.blur();
		}
		if (getPendingBrandCorrectionUpdates().length === 0) {
			setHasPendingCorrectionChanges(false);
			return null;
		}

		saveInFlightRef.current = true;
		setIsSavingCorrections(true);
		try {
			const data = await savePendingCorrections();
			toast.success('校正额已保存');
			return data;
		} catch (error) {
			console.error('保存品牌校正额失败', error);
			toast.error(error?.data?.message || error?.message || '保存校正额失败');
			throw error;
		} finally {
			saveInFlightRef.current = false;
			setIsSavingCorrections(false);
		}
	};
	handleSaveCorrectionsRef.current = handleSaveCorrections;

	useEffect(() => registerSave(() => handleSaveCorrectionsRef.current?.()), [registerSave]);

	useEffect(() => {
		setDraftStatus({ hasPending: hasPendingCorrectionChanges, isSaving: isSavingCorrections });
	}, [hasPendingCorrectionChanges, isSavingCorrections, setDraftStatus]);

	useEffect(
		() => () => {
			setDraftStatus({ hasPending: false, isSaving: false });
		},
		[setDraftStatus],
	);

	const orderCorrectionTotal = useMemo(
		() =>
			categoryCorrectionKeys.reduce((sum, key) => {
				const [brandName, categoryName] = key.split('::');
				return sum + (Number(getCategoryCorrectionAmount(brandName, categoryName)) || 0);
			}, 0),
		[categoryCorrectionKeys, getCategoryCorrectionAmount],
	);

	const nonGroupedTable = useAdvanceTable({
		data: useGroupedData ? [] : orderItems,
		columns,
		sortable: true,
		pagination: false,
	});
	const { footerCells, footerSpanGroups } = useMemo(
		() => buildProductListFooter(orderItems, orderCorrectionTotal),
		[orderItems, orderCorrectionTotal],
	);

	return (
		<EventModalMediaContent
				icon='box'
				heading='产品列表'
				headingAction={
					hasPendingCorrectionChanges ? (
						<button type='button' className='btn btn-primary btn-sm' disabled={actionDisabled || isSavingCorrections} onClick={() => handleSaveCorrections().catch(() => {})}>
							{isSavingCorrections ? '保存中...' : '保存校正额'}
						</button>
					) : null
				}
			>
				<div>
					{useGroupedData ? (
						<>
							{orderItemsGroupedByBrand.map((brandGroup, groupIndex) => (
								<BrandGroupTable
									key={brandGroup.brandName || groupIndex}
									brandName={brandGroup.brandName}
									items={brandGroup.items}
									columns={columns}
									getCategoryCorrectionAmount={getCategoryCorrectionAmount}
									onCategoryCorrectionRequest={canEditCorrections ? handleCategoryCorrectionChange : undefined}
								/>
							))}
							<OrderProductListSummary items={orderItems} correctionTotal={orderCorrectionTotal} />
						</>
					) : (
						<AdvanceTableProvider {...nonGroupedTable}>
							<AdvanceTableResponsive
								cardListClassName='order-modal-product-card-list'
								headerClassName='bg-warning-subtle text-nowrap align-middle'
								rowClassName='text-nowrap align-middle'
								footerCells={footerCells}
								footerSpanGroups={footerSpanGroups}
								tableProps={{
									className: 'fs-10 fw-semibold mb-0 overflow-hidden',
								}}
							/>
						</AdvanceTableProvider>
					)}
				</div>
		</EventModalMediaContent>
	);
};

export default ProductListDetail;
